// The chat answer layout (Lane 1405 v3 Part B). The browser twin of
// backend/helpers/answer_layout.py: the same rules, the same parts, the same
// fixtures (check 97 runs both and compares them). Keep the two in step.
//
// The model writes ordinary markdown with fixed headings; this reads it into
// display parts. It never changes the text. A part that breaks its rule is read
// as an ordinary section or text, so an answer is never blocked.
//
// No imports: check 97 runs this file in node as it is.

export const ANSWER_LAYOUT_VERSION = 'layout-v1';

const MAX_FIGURES = 3;
// A longer label with no detail is drawn as the detail (answer_layout.MAX_LABEL_WORDS).
const MAX_LABEL_WORDS = 6;
const MAX_FOLLOWUPS = 3;
const MAX_FOLLOWUP_CHARS = 200;

// [^\n] rather than '.', which in JavaScript also stops at \r (Python's does not).
const H1 = /^ {0,3}#[ \t]+([^\n]+?)[ \t]*$/;
const H2 = /^ {0,3}##[ \t]+([^\n]+?)(?:[ \t]+#+)?[ \t]*$/;
const H3 = /^ {0,3}###[ \t]+([^\n]+?)(?:[ \t]+#+)?[ \t]*$/;
const WARNING = /^ {0,3}>[ \t]?\*\*Warning:\*\*[ \t]*([^\n]*)$/i;
const QUOTE = /^ {0,3}>[ \t]?([^\n]*)$/;
const BASIS = /^Basis:[ \t]*([^\n]*?)[ \t]*$/;
const BULLET = /^ {0,3}[-*+][ \t]+([^\n]+?)[ \t]*$/;
const FIGURE = /^\*\*([^\n]+?)\*\*[ \t]*([^\n]*)$/;
const FIRST_SENTENCE = /^([^\n]+?[.!?])(?:[ \t]+|$)([^\n]*)$/;
// The chat table parser's GFM rules (backend conversation_deck_tables.py).
const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const BLOCK_START = /^ {0,3}(?:>|#{1,6}(?:\s|$)|(?:[-*+]|\d{1,9}[.)])\s|(?:-{3,}|_{3,}|\*{3,})\s*$)/;
const DELIMITER_CELL = /^\s*(:?)-+(:?)\s*$/;

const ROLES = {
  'key figures': 'figures',
  'ask next': 'followups',
  'suggested next steps': 'next_steps',
  'next steps': 'next_steps',
  'more detail': 'more_detail',
};
const LIST_ROLES = ['figures', 'followups'];

const isBlank = (line) => !line.trim();

function unescapedPipes(line) {
  const positions = [];
  let escaped = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (escaped) escaped = false;
    else if (ch === '\\') escaped = true;
    else if (ch === '|') positions.push(i);
  }
  return positions;
}

function splitCells(line) {
  const pipes = unescapedPipes(line);
  if (!pipes.length) return null;
  const bounds = [-1, ...pipes, line.length];
  let parts = [];
  for (let i = 0; i < bounds.length - 1; i += 1) parts.push(line.slice(bounds[i] + 1, bounds[i + 1]));
  if (line.trimStart().startsWith('|')) parts = parts.slice(1);
  if (line.trimEnd().endsWith('|') && parts.length && parts[parts.length - 1].trim() === '') {
    parts = parts.slice(0, -1);
  }
  return parts;
}

function isDelimiter(line) {
  const parts = splitCells(line);
  if (!parts || !parts.length) return false;
  return parts.every((part) => DELIMITER_CELL.test(part));
}

function isTableStart(lines, index) {
  if (index + 1 >= lines.length) return false;
  if (!unescapedPipes(lines[index]).length || isDelimiter(lines[index])) return false;
  return isDelimiter(lines[index + 1]);
}

// Python str.strip(chars) for one character.
function stripChar(text, ch) {
  let start = 0;
  let end = text.length;
  while (start < end && text[start] === ch) start += 1;
  while (end > start && text[end - 1] === ch) end -= 1;
  return text.slice(start, end);
}

export function fencedLines(lines) {
  const fenced = new Set();
  let open = null;
  lines.forEach((line, index) => {
    const match = FENCE.exec(line);
    if (open === null) {
      if (match) {
        open = match[1];
        fenced.add(index);
      }
    } else {
      fenced.add(index);
      if (match && match[1][0] === open[0] && match[1].length >= open.length
        && !stripChar(line.trim(), match[1][0])) {
        open = null;
      }
    }
  });
  return fenced;
}

function role(title) {
  const key = title.trim().replace(/:+$/, '').trim().toLowerCase();
  return ROLES[key] || 'section';
}

function trimRun(lines) {
  let start = 0;
  let end = lines.length - 1;
  while (start <= end && isBlank(lines[start][1])) start += 1;
  while (end >= start && isBlank(lines[end][1])) end -= 1;
  return start <= end ? [start, end] : null;
}

function makeParser(text) {
  const lines = text.split('\n');
  const fenced = fencedLines(lines);
  const st = { parts: [], section: null, block: null };

  const target = () => (st.section !== null ? st.section.blocks : st.parts);
  const openBlock = (kind, index, title = null, first = null) => {
    st.block = { kind, title, lines: [], line_start: index, state: 'pre', first };
    target().push(st.block);
  };
  const closeBlock = () => { st.block = null; };
  const closeSection = () => { closeBlock(); st.section = null; };
  const add = (index, line) => {
    if (st.block === null || st.block.kind === 'warning') {
      if (isBlank(line)) return;
      openBlock('text', index);
    }
    st.block.lines.push([index, line]);
  };

  function feedTitled(index, line) {
    const block = st.block;
    const blank = isBlank(line);
    if (block.state === 'pre') {
      if (!blank && isTableStart(lines, index)) block.state = 'table';
      block.lines.push([index, line]);
      return true;
    }
    if (block.state === 'table') {
      if (blank) block.state = 'gap';
      else if (BLOCK_START.test(line) || isTableStart(lines, index)) {
        closeBlock();
        return false;
      }
      block.lines.push([index, line]);
      return true;
    }
    if (block.state === 'gap') {
      if (blank) {
        block.lines.push([index, line]);
        return true;
      }
      if (BLOCK_START.test(line) || isTableStart(lines, index)) {
        closeBlock();
        return false;
      }
      block.state = 'note';
      block.lines.push([index, line]);
      return true;
    }
    if (blank) {
      closeBlock();
      return true;
    }
    block.lines.push([index, line]);
    return true;
  }

  function feed(index, seenText) {
    const line = lines[index];
    if (fenced.has(index)) {
      add(index, line);
      return;
    }
    let match;
    if (!seenText && !isBlank(line)) {
      match = H1.exec(line);
      if (match) {
        st.parts.push({ kind: 'headline', text: match[1], line_start: index, line_end: index });
        return;
      }
    }
    match = H2.exec(line);
    if (match) {
      closeSection();
      st.section = { kind: 'section', role: role(match[1]), title: match[1], blocks: [], line_start: index };
      st.parts.push(st.section);
      return;
    }
    if (st.section !== null && LIST_ROLES.includes(st.section.role)
      && !isBlank(line) && !BULLET.test(line)) {
      closeSection();
    }
    match = H3.exec(line);
    if (match) {
      closeBlock();
      const kind = st.section !== null && st.section.role === 'more_detail' ? 'fold' : 'titled';
      openBlock(kind, index, match[1]);
      return;
    }
    match = WARNING.exec(line);
    if (match) {
      closeBlock();
      openBlock('warning', index, null, match[1]);
      return;
    }
    if (st.block !== null && st.block.kind === 'warning') {
      if (QUOTE.test(line)) {
        st.block.lines.push([index, line]);
        return;
      }
      closeBlock();
    }
    const prevBlank = index === 0 || isBlank(lines[index - 1]);
    match = BASIS.exec(line);
    if (match && prevBlank) {
      closeSection();
      st.parts.push({ kind: 'basis', text: match[1], line_start: index, line_end: index });
      return;
    }
    if (st.block !== null && st.block.kind === 'titled' && feedTitled(index, line)) return;
    add(index, line);
  }

  return { lines, st, feed };
}

function tableAndNote(rows) {
  const lines = rows.map((row) => row[1]);
  if (!isTableStart(lines, 0)) return [null, null, null];
  let end = 0;
  while (end < lines.length && !isBlank(lines[end])) end += 1;
  const table = lines.slice(0, end).join('\n');
  const rest = lines.slice(end);
  while (rest.length && isBlank(rest[0])) rest.shift();
  if (!rest.length) return [table, null, rows[0][0]];
  if (rest.some(isBlank)) return [null, null, null];
  if (BLOCK_START.test(rest[0]) || isTableStart(rest, 0) || unescapedPipes(rest[0]).length) {
    return [null, null, null];
  }
  return [table, rest.map((line) => line.trim()).join(' '), rows[0][0]];
}

function finishBlock(block) {
  if (block.kind === 'warning') {
    const body = block.lines.map(([, line]) => QUOTE.exec(line)[1]);
    // The title is the first sentence of the first line only, so a quoted table
    // or list below it stays in the text (Codex L2 r2-2).
    const first = block.first.trim();
    const match = FIRST_SENTENCE.exec(first);
    const [title, rest] = match ? [match[1], match[2].trim()] : [first, ''];
    const text = [rest, ...body].join('\n').trim();
    const last = block.lines.length ? block.lines[block.lines.length - 1][0] : block.line_start;
    return { kind: 'warning', title, text, line_start: block.line_start, line_end: last, table_follows: false };
  }
  const kept = trimRun(block.lines);
  if (kept === null) {
    if (block.kind === 'text') return null;
    return {
      kind: block.kind, title: block.title, markdown: '', table: null, note: null, table_line: null,
      line_start: block.line_start, line_end: block.line_start,
    };
  }
  const rows = block.lines.slice(kept[0], kept[1] + 1);
  const markdown = rows.map((row) => row[1]).join('\n');
  if (block.kind === 'text') {
    return { kind: 'text', markdown, opening: false, line_start: rows[0][0], line_end: rows[rows.length - 1][0] };
  }
  const [table, note, tableLine] = tableAndNote(rows);
  return {
    kind: block.kind, title: block.title, markdown, table, note, table_line: tableLine,
    line_start: block.line_start, line_end: rows[rows.length - 1][0],
  };
}

function bullets(blocks) {
  if (blocks.length !== 1 || blocks[0].kind !== 'text') return null;
  const items = [];
  for (const line of blocks[0].markdown.split('\n')) {
    if (isBlank(line)) continue;
    const match = BULLET.exec(line);
    if (!match) return null;
    items.push(match[1]);
  }
  return items;
}

function figure(text) {
  const match = FIGURE.exec(text);
  if (!match || !/\d/.test(match[1])) return null;
  const rest = match[2].trim();
  const cut = rest.indexOf(': ');
  let label = (cut >= 0 ? rest.slice(0, cut) : rest).replace(/:+$/, '').trim();
  let detail = (cut >= 0 ? rest.slice(cut + 2) : '').trim();
  if (!label) return null;
  if (!detail && label.split(/\s+/).length > MAX_LABEL_WORDS) {
    detail = label;
    label = '';
  }
  return { value: match[1].trim(), label, detail };
}

function leadingTable(block) {
  if (block.kind === 'titled' || block.kind === 'fold') return block.table !== null;
  if (block.kind === 'text') return isTableStart(block.markdown.split('\n'), 0);
  return false;
}

function linkWarnings(blocks) {
  blocks.forEach((block, index) => {
    if (block.kind === 'warning') {
      const next = index + 1 < blocks.length ? blocks[index + 1] : null;
      block.table_follows = Boolean(next !== null && leadingTable(next));
    }
  });
}

function finishSection(section) {
  const blocks = section.blocks.map(finishBlock).filter((block) => block !== null);
  linkWarnings(blocks);
  const lineEnd = Math.max(section.line_start, ...blocks.map((block) => block.line_end));
  const base = { line_start: section.line_start, line_end: lineEnd };
  let sectionRole = section.role;
  if (sectionRole === 'figures') {
    const items = bullets(blocks);
    const figures = items && items.length ? items.map(figure) : null;
    if (figures && figures.length <= MAX_FIGURES && figures.every(Boolean)) {
      return { kind: 'figures', title: section.title, items: figures, ...base };
    }
    sectionRole = 'section';
  }
  if (sectionRole === 'followups') {
    const items = bullets(blocks);
    if (items && items.length && items.length <= MAX_FOLLOWUPS
      && items.every((item) => item.endsWith('?') && Array.from(item).length <= MAX_FOLLOWUP_CHARS
        && !unescapedPipes(item).length)) {
      return { kind: 'followups', title: section.title, items, ...base };
    }
    sectionRole = 'section';
  }
  return { kind: 'section', role: sectionRole, title: section.title, blocks, ...base };
}

// Pure. The display parts of one answer, or null when the version is not one
// this reader knows (the answer is then drawn the legacy way).
export function parseAnswerLayout(text, version) {
  if (version !== ANSWER_LAYOUT_VERSION || typeof text !== 'string') return null;
  const parser = makeParser(text);
  let seenText = false;
  for (let index = 0; index < parser.lines.length; index += 1) {
    parser.feed(index, seenText);
    if (!isBlank(parser.lines[index])) seenText = true;
  }
  const parts = [];
  for (const part of parser.st.parts) {
    if (part.kind === 'headline' || part.kind === 'basis') parts.push(part);
    else if (part.kind === 'section') parts.push(finishSection(part));
    else {
      const done = finishBlock(part);
      if (done !== null) parts.push(done);
    }
  }
  linkWarnings(parts);
  if (parts.length > 1 && parts[0].kind === 'headline' && parts[1].kind === 'text') {
    parts[1].opening = true;
  }
  return { version: ANSWER_LAYOUT_VERSION, parts };
}
